// ... existing imports ...
import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Textarea,
  useToast,
  Image,
  Text,
} from '@chakra-ui/react';
import { Select, chakraComponents, MultiValue } from 'chakra-react-select';
import { MdKeyboardBackspace } from 'react-icons/md';
import { pxToRem } from '../../utils';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiUrl } from '../../utils/utils';
import { getFirebaseIdToken } from '../../utils/utils';
import axios from 'axios';
import { HttpsProvider } from '../../redux/httpsProviders/types';
import {
  editApplication,
  getImageUrl,
} from '../../utils/app-builder/register-application';
import { FiImage } from 'react-icons/fi';

export const ApplicationEditScreen = () => {
  const { appId: applicationId } = useParams();

  const [formData, setFormData] = useState<any>({
    name: '',
    description: '',
    appImageUrl: '',
    httpProviderId: [],
  });
  const [availableProviders, setAvailableProviders] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState<
    MultiValue<{ value: string; label: string }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const applicationImageUploadRef = useRef<HTMLInputElement>(null);
  const [appImageError, setAppImageError] = useState<string>('');
  const [hasChangedImage, setHasChangedImage] = useState<boolean>(false);

  const toast = useToast();
  const navigate = useNavigate();

  // Fetch application data
  const fetchApplication = async () => {
    try {
      setLoading(true);
      const token = await getFirebaseIdToken();
      const { data } = await axios.get(
        `${getApiUrl()}/api/applications/${applicationId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (data?.isSuccess) {
        setFormData(data?.application);

        setSelectedProviders(
          data?.application?.httpProvider?.map((provider: HttpsProvider) => ({
            label: provider.name,
            value: provider.httpProviderId,
          }))
        );
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch application details',
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch available providers
  const fetchProviders = async () => {
    try {
      const token = await getFirebaseIdToken();
      const { data } = await axios.get(`${getApiUrl()}/api/providers/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data?.isSuccess) {
        const providerOptions = data.providers.map((provider: HttpsProvider) => ({
          value: provider.httpProviderId,
          label: provider.name,
        }));
        setAvailableProviders(providerOptions);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch providers',
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  useEffect(() => {
    fetchApplication();
    fetchProviders();
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        setFormData((prev: any) => ({
          ...prev,
          updatedImage: file,
        }));
      };
      reader.readAsDataURL(file);
      setAppImageError('');
      setHasChangedImage(true);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProvidersChange = (
    selectedOptions: MultiValue<{ value: string; label: string }>
  ) => {
    setSelectedProviders(selectedOptions);
  };

  const handleSubmit = async () => {
    const selectedProviderIds = selectedProviders.map((p: any) => p?.value);
    let originalImageUrl = formData.appImageUrl;

    if (hasChangedImage && formData.appImageUrl !== imagePreview) {
      const data = await getImageUrl(imagePreview, formData.appId);
      if (data?.error) {
        setAppImageError(data?.error);
        toast({
          title: 'Failed to upload image',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }
      originalImageUrl = data?.url as string;
    }

    const newAppData = { ...formData, httpProviderIds: selectedProviderIds };
    newAppData.appImageUrl = originalImageUrl;

    const data = await editApplication(newAppData);

    if (data?.isSuccess) {
      toast({
        title: 'Application updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return navigate(-1);
    }

    toast({
      title: 'Failed to update application',
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  return (
    <Box p={4}>
      <Box>
        <Box
          mt="32px"
          p="20px"
          borderRadius="lg"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="borderColor"
          bgColor={'white'}
        >
          <IconButton
            variant="outline"
            colorScheme="blue"
            aria-label=""
            onClick={() => navigate(-1)}
            icon={<MdKeyboardBackspace />}
            width="fit-content"
            mb={pxToRem(20)}
          />
          <Heading as="h2" size="xl" mb="20px">
            Application Details
          </Heading>
          <Box p={4}>
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              <FormControl mb={4}>
                <FormLabel>Application Name</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter application name"
                />
              </FormControl>
            </Box>
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter application description"
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>App Logo</FormLabel>
              <Input
                type="file"
                accept="image/*"
                hidden
                ref={applicationImageUploadRef}
                onChange={handleImageChange}
              />
              <Button
                _hover={{ bgColor: 'gray.50' }}
                borderRadius="16px"
                bgColor="white"
                borderWidth="2px"
                borderStyle="dashed"
                borderColor="gray.200"
                h={'150px'}
                w={'150px'}
                onClick={() => applicationImageUploadRef?.current?.click()}
              >
                <Flex direction="column">
                  {imagePreview || formData.appImageUrl ? (
                    <Box
                      borderWidth={1}
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="md"
                    >
                      <Image
                        src={imagePreview || formData.appImageUrl}
                        h={'100px'}
                        w={'100px'}
                        alt="App logo"
                      />
                    </Box>
                  ) : (
                    <Flex
                      direction="column"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                    >
                      <FiImage fontSize="2rem" color="gray" />
                      <Text ml="5px" color="gray.500" fontSize={'14px'} fontWeight={400}>
                        App Logo
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </Button>
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Selected Providers</FormLabel>
              <Select
                isMulti
                options={availableProviders}
                value={selectedProviders}
                onChange={handleProvidersChange}
                placeholder="Select providers..."
                closeMenuOnSelect={false}
                chakraStyles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: '0.375rem',
                  }),
                  multiValue: (provided) => ({
                    ...provided,
                    borderRadius: '0.25rem',
                  }),
                }}
              />
            </FormControl>

            <Button colorScheme="blue" isLoading={isSubmitting} onClick={handleSubmit}>
              Save Changes
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
